import { useRef, useState } from 'react';

import { Download, Loader2, Plus, Star, Pencil, Trash2, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import {
  useCreateDashboardConfig,
  useDashboardConfigList,
  useDeleteDashboardConfig,
  useSetDefaultDashboardConfig,
} from '@/hooks/useDashboardConfigs';
import { usePageTitle } from '@/hooks/usePageTitle';
import { dashboardConfigApi } from '@/lib/api/dashboard-config.api';

import { dashboardConfigSchema } from '@repo/shared-types';

export const DashboardConfigsPage = () => {
  usePageTitle('Dashboard Configs');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: configs, isLoading } = useDashboardConfigList();
  const setDefaultMutation = useSetDefaultDashboardConfig();
  const deleteMutation = useDeleteDashboardConfig();
  const createMutation = useCreateDashboardConfig();
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = async (id: string, name: string) => {
    try {
      const detail = await dashboardConfigApi.getById(id);
      const blob = new Blob([JSON.stringify(detail.config, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name
        .replace(/[^a-zA-Z0-9-_ ]/g, '')
        .replace(/\s+/g, '-')
        .toLowerCase()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to export config.' });
    }
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        const config = dashboardConfigSchema.parse(json);
        const name = file.name.replace(/\.json$/i, '').replace(/[-_]/g, ' ');
        await createMutation.mutateAsync({ name, config });
        toast({
          variant: 'success',
          title: 'Imported',
          description: `Dashboard config "${name}" imported.`,
        });
      } catch {
        toast({
          variant: 'destructive',
          title: 'Invalid file',
          description: 'The file is not a valid dashboard config JSON.',
        });
      }
    };
    reader.readAsText(file);
    // Reset so the same file can be re-imported
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSetDefault = async (id: string, name: string) => {
    try {
      await setDefaultMutation.mutateAsync(id);
      toast({
        variant: 'success',
        title: 'Default updated',
        description: `"${name}" is now the active dashboard.`,
      });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to set default config.',
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast({
        variant: 'success',
        title: 'Deleted',
        description: `"${deleteTarget.name}" has been deleted.`,
      });
      setDeleteTarget(null);
    } catch {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to delete config. Default configs cannot be deleted.',
      });
      setDeleteTarget(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Settings</p>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard Configs</h1>
          <p className="text-muted-foreground max-w-3xl">
            Manage dashboard configurations. Each config defines what metrics, tree nodes, KPI
            cards, and funnel stages are shown on the Analytics page. The default config is used for
            all users.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
            <Upload className="h-4 w-4" />
            Import JSON
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            className="hidden"
            onChange={handleImport}
          />
          <Button onClick={() => navigate('/dashboard-configs/new')}>
            <Plus className="h-4 w-4" />
            New Config
          </Button>
        </div>
      </header>

      {!configs?.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              No dashboard configs found. Create one to get started.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {configs.map((cfg) => (
            <Card key={cfg.id} className={cfg.is_default ? 'border-primary/40' : ''}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-base">{cfg.name}</CardTitle>
                    <CardDescription className="text-xs">
                      Updated {new Date(cfg.updated_at).toLocaleDateString()}
                    </CardDescription>
                  </div>
                  {cfg.is_default && (
                    <Badge variant="default" className="shrink-0">
                      <Star className="mr-1 h-3 w-3" />
                      Default
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/dashboard-configs/${cfg.id}`)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExport(cfg.id, cfg.name)}
                  >
                    <Download className="h-3.5 w-3.5" />
                    Export
                  </Button>
                  {!cfg.is_default && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSetDefault(cfg.id, cfg.name)}
                        disabled={setDefaultMutation.isPending}
                      >
                        <Star className="h-3.5 w-3.5" />
                        Set Default
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteTarget({ id: cfg.id, name: cfg.name })}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <DeleteConfirmationDialog
        open={!!deleteTarget}
        resourceName="dashboard config"
        resourceIdentifier={deleteTarget?.name ? `"${deleteTarget.name}"` : undefined}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};
